import React, { useState } from 'react'

import {
  BaseUserProfile,
  FoodRestriction,
  MacrosDistribution,
  Meal,
  MealOfDayDivision,
  RecipeTag,
  RecommenderSettings,
  SearchIngredient,
  SimulationProfile,
  UserProfile,
} from '@/api/types'
import { nutritionalNeedsQuery } from '@/api/mealPlan'

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import UserMealPlan from './MealPlan/CreateMealPlan'
import { PageEditHeader } from '@/components/PageEditHeader'
import ProfileSelector from './ProfileSelector/ProfileSelector'
import useSimulationProfile from './ProfileSelector/useSimulationProfile'
import { formatISO } from 'date-fns'
import RecommenderSettingsDialog from './RecommenderSettingsDialog/RecommenderSettingsDialog'
import UserProfileSelector from './UserProfileSelector'
import { UserProfilePanel } from '@/features/UserProfilePanel/UserProfilePanel'

type Props = {
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  defaultMealOfDayDivisions: MealOfDayDivision[]
  energyDistribution: MacrosDistribution
  ingredients: SearchIngredient[] //TODO
  simulationProfiles: SimulationProfile[]
  lang?: string
}

export default function MealSimulationIndex({
  tags,
  allergens,
  defaultMealOfDayDivisions,
  energyDistribution,
  ingredients,
  simulationProfiles,
  lang,
}: Props) {
  const { t } = useTranslation()

  const [recommenderSettings, setRecommenderSettings] = React.useState<
    RecommenderSettings | undefined
  >(undefined)

  const [planDate] = useState(formatISO(new Date(), { representation: 'date' }))

  const { simulationProfile, setSimulationProfile } = useSimulationProfile(
    energyDistribution,
    defaultMealOfDayDivisions,
  )

  const userProfile = simulationProfile.userProfile

  function setUserProfile(userProfile: UserProfile) {
    setSimulationProfile({ ...simulationProfile, userProfile })
  }

  const { data: nutritionalNeeds } = useQuery(nutritionalNeedsQuery(userProfile))

  const onMealPreferencesChange = (
    selectedMeals: Meal[],
    mealOfDayDivisions: MealOfDayDivision[],
  ) =>
    setSimulationProfile({
      ...simulationProfile,
      userProfile: {
        ...simulationProfile.userProfile,
        household: {
          ...simulationProfile.userProfile.household,
          selectedMeals: selectedMeals,
        },
        mealOfDayDivisions,
      },
    })

  function updateHouseholdMembers(householdMembers: BaseUserProfile[]) {
    setSimulationProfile({
      ...simulationProfile,
      userProfile: {
        ...simulationProfile.userProfile,
        household: {
          ...simulationProfile.userProfile.household,
          users: householdMembers,
        },
      },
      scope: {
        ...simulationProfile.scope,
        householdMembers,
      },
    })
  }

  return (
    <>
      <PageEditHeader title={t('mealSimulation.title')}>
        <UserProfileSelector onSelect={(userProfile) => setSimulationProfile({ userProfile })} />
        <div style={{ alignSelf: 'center', fontWeight: '500' }}>Profile</div>
        <ProfileSelector
          simulationProfile={simulationProfile}
          simulationProfiles={simulationProfiles}
          onChange={setSimulationProfile}
        />
        <RecommenderSettingsDialog
          recommenderSettings={recommenderSettings}
          setRecommenderSettings={setRecommenderSettings}
        />
      </PageEditHeader>
      <UserProfilePanel
        userProfile={simulationProfile.userProfile}
        nutritionalNeeds={nutritionalNeeds}
        energyDistribution={energyDistribution}
        tags={tags}
        allergens={allergens}
        onChangeProfile={setUserProfile}
        onChangeMealPreferences={onMealPreferencesChange}
        onChangeHouseholdMembers={updateHouseholdMembers}
      />
      <UserMealPlan
        userProfile={simulationProfile.userProfile}
        planDate={planDate}
        householdMembers={simulationProfile.userProfile.household.users || []}
        userNutritionalNeeds={nutritionalNeeds}
        recommenderSettings={recommenderSettings}
        lang={lang}
        ingredients={ingredients}
      />
    </>
  )
}
